import { z } from 'zod';

import { InvoicingDefaultLateMaxDaysTypeEnum } from '__generated__/GQL';
import zodLocales from 'common/zod/locales';
import { isValidEmail } from 'components/LegacyInput/utils';
import { SIXTY_DAYS_LIMIT } from 'features/Work/constants';
import {
  getLateFeePercentageFormSchema,
  issuerEmailRequiredFormSchema,
  legalMentionsRequiredFormSchema,
  quoteValidityPeriodSchema,
} from 'features/Work/form/optionForm.schema';

interface InvoicePresetsFormSchemaParameters {
  minLatePaymentFee: number;
}

export const getInvoicePresetsFormSchema = ({
  minLatePaymentFee,
}: InvoicePresetsFormSchemaParameters) =>
  z
    .object({
      issuerEmail: issuerEmailRequiredFormSchema,
      issuerEmailEnabled: z.boolean(),
      lateMaxDaysOther: z
        .number()
        .int({ message: zodLocales.errors.integer })
        .positive({ message: zodLocales.errors.positive })
        .max(SIXTY_DAYS_LIMIT, {
          message: zodLocales.formatString(
            zodLocales.errors.maxValue,
            SIXTY_DAYS_LIMIT,
          ),
        })
        .nullable(),
      lateMaxDaysType: z.nativeEnum(InvoicingDefaultLateMaxDaysTypeEnum),
      legalMentions: legalMentionsRequiredFormSchema,
      legalMentionsEnabled: z.boolean(),
      paymentLateFee: getLateFeePercentageFormSchema(minLatePaymentFee),
      quoteValidityDays: quoteValidityPeriodSchema,
      quoteValidityDaysEnabled: z.boolean(),
    })

    .refine(
      ({ issuerEmail, issuerEmailEnabled }) =>
        issuerEmailEnabled ? Boolean(issuerEmail.trim()) : true,
      {
        message: zodLocales.errors.required,
        path: ['issuerEmail'],
      },
    )

    .refine(
      ({ issuerEmail, issuerEmailEnabled }) =>
        issuerEmail && issuerEmailEnabled ? isValidEmail(issuerEmail) : true,
      { message: zodLocales.errors.invalidEmail, path: ['issuerEmail'] },
    )

    .refine(
      ({ legalMentions, legalMentionsEnabled }) =>
        legalMentionsEnabled ? Boolean(legalMentions.trim()) : true,
      {
        message: zodLocales.errors.required,
        path: ['legalMentions'],
      },
    )

    .refine(
      ({ quoteValidityDays, quoteValidityDaysEnabled }) =>
        quoteValidityDaysEnabled ? quoteValidityDays !== null : true,
      {
        message: zodLocales.errors.required,
        path: ['quoteValidityDays'],
      },
    )

    .refine(
      ({ lateMaxDaysOther, lateMaxDaysType }) =>
        lateMaxDaysType === InvoicingDefaultLateMaxDaysTypeEnum.Other
          ? lateMaxDaysOther !== null
          : true,
      { message: zodLocales.errors.required, path: ['lateMaxDaysOther'] },
    );

export type InvoicePresetsFormSchema = z.infer<
  ReturnType<typeof getInvoicePresetsFormSchema>
>;
